import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenantContext } from './useTenantContext';
import { useVisibility } from './useVisibility';
import { useMemo } from 'react';
import { formatDateInputInTz } from '@/utils/timezone';
import { fetchAllRows } from '@/utils/fetchAllRows';

// Cost = what the company paid (part_cost); revenue = what the customer was charged
// (sell_price); margin = revenue - cost. Jobs closed before the sell-price migration
// carry sell = cost, so their margin is zero by construction.
interface PartRecord {
  id: string;
  part_name: string;
  part_cost: number;
  sell_price: number;
  quantity: number;
  part_source: string | null;
  job_id: string;
  metro_id: string | null;
  metro_name: string | null;
  tech_id: string | null;
  tech_name: string | null;
}

interface PartSummary {
  totalCost: number;
  totalRevenue: number;
  totalMargin: number;
  marginPct: number;
  uniqueParts: number;
  averageCostPerJob: number;
  totalQuantity: number;
  jobsWithParts: number;
}

interface TopPart {
  name: string;
  quantity: number;
  totalCost: number;
  totalRevenue: number;
  totalMargin: number;
}

interface MetroPartDetail {
  name: string;
  quantity: number;
  unitCost: number;
  unitSell: number;
  totalCost: number;
  revenue: number;
  margin: number;
}

interface MetroBreakdown {
  metroId: string;
  metroName: string;
  partsCount: number;
  totalCost: number;
  totalRevenue: number;
  totalMargin: number;
  jobCount: number;
  averagePerJob: number;
  parts: MetroPartDetail[];
}

interface TechBreakdown {
  techId: string;
  techName: string;
  partsCount: number;
  totalCost: number;
  totalRevenue: number;
  totalMargin: number;
  jobCount: number;
  averagePerJob: number;
}

interface SourceBreakdown {
  source: string;
  totalCost: number;
  totalRevenue: number;
  quantity: number;
  percentage: number;
}

export interface PartsReportData {
  summary: PartSummary;
  topPartsByQuantity: TopPart[];
  topPartsByValue: TopPart[];
  byMetro: MetroBreakdown[];
  byTech: TechBreakdown[];
  bySource: SourceBreakdown[];
}

interface UsePartsReportProps {
  startDate: Date;
  endDate: Date;
  timezone: string;
}

const SOURCE_LABELS: Record<string, string> = {
  company_stock: 'Company Stock',
  tech_personal: 'Technician Personal',
};

export function usePartsReport({ startDate, endDate, timezone }: UsePartsReportProps) {
  const { tenantId: companyId } = useTenantContext();
  const { canSeeCompanyFinancials } = useVisibility();

  const { data: rawData, isLoading, error } = useQuery({
    queryKey: ['reports', 'parts-report', companyId, startDate.toISOString(), endDate.toISOString()],
    queryFn: async () => {
      if (!companyId) return [];

      // Fetch job_parts with job details for date filtering
      const parts = await fetchAllRows((from, to) => supabase
        .from('job_parts')
        .select(`
          id,
          part_name,
          part_cost,
          sell_price,
          quantity,
          part_source,
          job_id,
          jobs!inner (
            id,
            job_date,
            metro_id,
            assigned_tech_id,
            metros ( id, name ),
            techs ( id, name )
          )
        `)
        .eq('company_id', companyId)
        .eq('jobs.status', 'completed')
        .gte('jobs.job_date', formatDateInputInTz(startDate, timezone))
        .lte('jobs.job_date', formatDateInputInTz(endDate, timezone))
        .order('id')
        .range(from, to));

      // Transform to flat structure
      return (parts || []).map((p): PartRecord => ({
        id: p.id,
        part_name: p.part_name,
        part_cost: p.part_cost,
        sell_price: p.sell_price ?? p.part_cost,
        quantity: p.quantity,
        part_source: p.part_source,
        job_id: p.job_id,
        metro_id: (p.jobs as any)?.metro_id || null,
        metro_name: (p.jobs as any)?.metros?.name || null,
        tech_id: (p.jobs as any)?.assigned_tech_id || null,
        tech_name: (p.jobs as any)?.techs?.name || null,
      }));
    },
    enabled: !!companyId && canSeeCompanyFinancials,
  });

  const reportData = useMemo((): PartsReportData => {
    const parts = rawData || [];
    const costOf = (p: PartRecord) => p.part_cost * p.quantity;
    const revenueOf = (p: PartRecord) => p.sell_price * p.quantity;

    // Summary calculations
    const totalCost = parts.reduce((sum, p) => sum + costOf(p), 0);
    const totalRevenue = parts.reduce((sum, p) => sum + revenueOf(p), 0);
    const totalMargin = totalRevenue - totalCost;
    const totalQuantity = parts.reduce((sum, p) => sum + p.quantity, 0);
    const uniquePartNames = new Set(parts.map(p => p.part_name.toLowerCase().trim()));
    const uniqueJobs = new Set(parts.map(p => p.job_id));
    const jobsWithParts = uniqueJobs.size;
    const averageCostPerJob = jobsWithParts > 0 ? totalCost / jobsWithParts : 0;

    const summary: PartSummary = {
      totalCost,
      totalRevenue,
      totalMargin,
      marginPct: totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0,
      uniqueParts: uniquePartNames.size,
      averageCostPerJob,
      totalQuantity,
      jobsWithParts,
    };

    // Top parts by quantity / value
    const partsByName = new Map<string, { quantity: number; totalCost: number; totalRevenue: number }>();
    parts.forEach(p => {
      const key = p.part_name.toLowerCase().trim();
      const existing = partsByName.get(key) || { quantity: 0, totalCost: 0, totalRevenue: 0 };
      partsByName.set(key, {
        quantity: existing.quantity + p.quantity,
        totalCost: existing.totalCost + costOf(p),
        totalRevenue: existing.totalRevenue + revenueOf(p),
      });
    });

    const allParts: TopPart[] = Array.from(partsByName.entries()).map(([name, data]) => ({
      name: parts.find(p => p.part_name.toLowerCase().trim() === name)?.part_name || name,
      quantity: data.quantity,
      totalCost: data.totalCost,
      totalRevenue: data.totalRevenue,
      totalMargin: data.totalRevenue - data.totalCost,
    }));

    const topPartsByQuantity = [...allParts]
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    const topPartsByValue = [...allParts]
      .sort((a, b) => b.totalCost - a.totalCost)
      .slice(0, 10);

    // Metro breakdown with parts detail
    const metroMap = new Map<string, {
      name: string;
      cost: number;
      revenue: number;
      count: number;
      jobs: Set<string>;
      partMap: Map<string, { qty: number; cost: number; revenue: number; unitCost: number; unitSell: number; displayName: string }>;
    }>();

    parts.forEach(p => {
      if (!p.metro_id) return;
      const existing = metroMap.get(p.metro_id) || {
        name: p.metro_name || 'Unknown',
        cost: 0,
        revenue: 0,
        count: 0,
        jobs: new Set<string>(),
        partMap: new Map()
      };
      existing.cost += costOf(p);
      existing.revenue += revenueOf(p);
      existing.count += p.quantity;
      existing.jobs.add(p.job_id);

      // Aggregate parts by name within this metro
      const partKey = p.part_name.toLowerCase().trim();
      const existingPart = existing.partMap.get(partKey) || {
        qty: 0,
        cost: 0,
        revenue: 0,
        unitCost: p.part_cost,
        unitSell: p.sell_price,
        displayName: p.part_name
      };
      existingPart.qty += p.quantity;
      existingPart.cost += costOf(p);
      existingPart.revenue += revenueOf(p);
      existing.partMap.set(partKey, existingPart);

      metroMap.set(p.metro_id, existing);
    });

    const byMetro: MetroBreakdown[] = Array.from(metroMap.entries())
      .map(([id, data]) => ({
        metroId: id,
        metroName: data.name,
        partsCount: data.count,
        totalCost: data.cost,
        totalRevenue: data.revenue,
        totalMargin: data.revenue - data.cost,
        jobCount: data.jobs.size,
        averagePerJob: data.jobs.size > 0 ? data.cost / data.jobs.size : 0,
        parts: Array.from(data.partMap.values())
          .map(p => ({
            name: p.displayName,
            quantity: p.qty,
            unitCost: p.unitCost,
            unitSell: p.unitSell,
            totalCost: p.cost,
            revenue: p.revenue,
            margin: p.revenue - p.cost,
          }))
          .sort((a, b) => b.totalCost - a.totalCost),
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    // Tech breakdown
    const techMap = new Map<string, { name: string; cost: number; revenue: number; count: number; jobs: Set<string> }>();
    parts.forEach(p => {
      if (!p.tech_id) return;
      const existing = techMap.get(p.tech_id) || {
        name: p.tech_name || 'Unknown',
        cost: 0,
        revenue: 0,
        count: 0,
        jobs: new Set<string>()
      };
      existing.cost += costOf(p);
      existing.revenue += revenueOf(p);
      existing.count += p.quantity;
      existing.jobs.add(p.job_id);
      techMap.set(p.tech_id, existing);
    });

    const byTech: TechBreakdown[] = Array.from(techMap.entries())
      .map(([id, data]) => ({
        techId: id,
        techName: data.name,
        partsCount: data.count,
        totalCost: data.cost,
        totalRevenue: data.revenue,
        totalMargin: data.revenue - data.cost,
        jobCount: data.jobs.size,
        averagePerJob: data.jobs.size > 0 ? data.cost / data.jobs.size : 0,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    // Source breakdown
    const sourceMap = new Map<string, { cost: number; revenue: number; quantity: number }>();
    parts.forEach(p => {
      const source = p.part_source || 'Unknown';
      const existing = sourceMap.get(source) || { cost: 0, revenue: 0, quantity: 0 };
      existing.cost += costOf(p);
      existing.revenue += revenueOf(p);
      existing.quantity += p.quantity;
      sourceMap.set(source, existing);
    });

    const bySource: SourceBreakdown[] = Array.from(sourceMap.entries())
      .map(([source, data]) => ({
        source: SOURCE_LABELS[source] ?? source,
        totalCost: data.cost,
        totalRevenue: data.revenue,
        quantity: data.quantity,
        percentage: totalCost > 0 ? (data.cost / totalCost) * 100 : 0,
      }))
      .sort((a, b) => b.totalCost - a.totalCost);

    return {
      summary,
      topPartsByQuantity,
      topPartsByValue,
      byMetro,
      byTech,
      bySource,
    };
  }, [rawData]);

  return {
    data: reportData,
    isLoading,
    error,
  };
}
